import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { FileText, Loader2, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { getActivityLogs } from '../actions/activityLogActions';
import CascadingDropdownFilter from '../components/CascadingDropdownFilter';
import AuditLogPanel from '../components/AuditLogPanel';
import styles from '../styles/ActivityLog.module.css';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';
import { usePrivilege } from '../hooks/usePrivilege';

const ROLE_HIERARCHY = [
  { key: 'company_id', label: 'Company', role: 'company' },
  { key: 'admin_id', label: 'Admin', role: 'admin' },
  { key: 'super_stokez_id', label: 'Super Stokez', role: 'super_stokez' },
  { key: 'stokez_id', label: 'Stokez', role: 'stokez' },
  { key: 'agent_id', label: 'Agent', role: 'agent' },
  { key: 'player_id', label: 'Player', role: 'player' },
];

const AUDIT_TABS = [
  { key: 'player', label: 'Player Activity' },
  { key: 'admin', label: 'Admin Actions', categories: ['player_risk', 'user_mgmt', 'game'] },
  { key: 'balance', label: 'Balance', categories: ['balance'] },
  { key: 'logins', label: 'Logins', categories: ['auth'] },
  { key: 'results', label: 'Results', categories: ['result'], restricted: true },
  { key: 'config', label: 'Settings & Privileges', categories: ['settings', 'privilege'], restricted: true },
  { key: 'security', label: 'Security', categories: ['security'], restricted: true },
  { key: 'system', label: 'System', categories: ['system'], restricted: true },
  { key: 'all', label: 'All Changes', categories: null },
];

const ActivityLog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = usePrivilege();
  const [serverRestrictedAllowed, setServerRestrictedAllowed] = useState(null);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlTarget = params.get('target') || '';
  const restrictedAllowed = serverRestrictedAllowed ?? can('report.auditAdmin');
  const tabs = useMemo(
    () => AUDIT_TABS.filter((t) => !t.restricted || restrictedAllowed),
    [restrictedAllowed]
  );
  const requestedTab = params.get('tab') || (urlTarget ? 'all' : 'player');
  const activeTab = tabs.find((t) => t.key === requestedTab) || tabs[0];

  const selectTab = (key) => {
    const next = new URLSearchParams(location.search);
    next.set('tab', key);
    navigate({ search: next.toString() }, { replace: true });
  };

  const handleMeta = useCallback(({ restrictedAllowed: allowed }) => {
    setServerRestrictedAllowed(allowed);
  }, []);

  return (
    <div className={styles.activityLog}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FileText size={24} className={styles.headerIcon} />
          <h1 className={styles.title}>Activity &amp; Audit Log</h1>
        </div>
      </div>

      <div className={styles.tabBar} role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === activeTab.key}
            className={t.key === activeTab.key ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => selectTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab.key === 'player' ? (
        <PlayerActivityView />
      ) : (
        <AuditLogPanel
          key={activeTab.key}
          categories={activeTab.categories}
          initialTarget={urlTarget}
          defaultDays={urlTarget ? 0 : 7}
          exportName={`audit-${activeTab.key}`}
          onMeta={handleMeta}
        />
      )}
    </div>
  );
};

const PlayerActivityView = () => {
  const dispatch = useDispatch();
  const { canShowField } = useFieldPrivilege();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [roleFilter, setRoleFilter] = useState({});

  const { activityLogs, loading, error } = useSelector((state) => {
    return state.activityLog;
  });

  const showSlno = canShowField('reportActivityLog', 'slno', 'list');
  const showUserId = canShowField('reportActivityLog', 'user_id', 'list');
  const showLogDate = canShowField('reportActivityLog', 'log_date', 'list');
  const showLogTime = canShowField('reportActivityLog', 'log_time', 'list');
  const showActivity = canShowField('reportActivityLog', 'activity', 'list');
  const showWinAmount = canShowField('reportActivityLog', 'win_amount', 'list');
  const visibleColCount = [
    showSlno, showUserId, showLogDate, showLogTime, showActivity, showWinAmount,
  ].filter(Boolean).length;

  const handleSelectionChange = useCallback((selections) => {
    setRoleFilter(selections || {});
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    dispatch(getActivityLogs(roleFilter));
  }, [dispatch, roleFilter]);

  const data = useMemo(() => {
    return Array.isArray(activityLogs) ? activityLogs : [];
  }, [activityLogs]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(data.length / pageSize) || 1;
  }, [data.length, pageSize]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageInput = e.target.querySelector('input[type="number"]');
    if (pageInput) {
      const page = parseInt(pageInput.value, 10);
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    }
  };

  return (
    <>
      <Box mb={4} p={3} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
        <CascadingDropdownFilter
          onSelectionChange={handleSelectionChange}
          initialValues={{}}
          userHierarchy={ROLE_HIERARCHY}
          roleBeingCreated="player"
        />
      </Box>

      {loading ? (
        <div className={styles.loadingContainer}>
          <Loader2 className={styles.spinner} size={48} />
        </div>
      ) : error ? (
        <div className={styles.errorAlert}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {showSlno && <th>SL NO</th>}
                  {showUserId && <th>User ID</th>}
                  {showLogDate && <th>Log Date</th>}
                  {showLogTime && <th>Log Time</th>}
                  {showActivity && <th>Activity</th>}
                  {showWinAmount && <th>Win Amount</th>}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColCount || 1} className={styles.emptyState}>
                      No activity logs found.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, index) => {
                    const globalIndex = (currentPage - 1) * pageSize + index;
                    return (
                      <tr key={index}>
                        {showSlno && <td>{row.slno || globalIndex + 1}</td>}
                        {showUserId && <td>{row.user_id || '-'}</td>}
                        {showLogDate && <td>{row.log_date ? new Date(row.log_date).toLocaleDateString() : '-'}</td>}
                        {showLogTime && <td>{row.log_time || '-'}</td>}
                        {showActivity && <td>{row.activity || '-'}</td>}
                        {showWinAmount && (
                          <td>{row.win_amount !== null && row.win_amount !== undefined ? row.win_amount : '-'}</td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {data.length > 0 && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationLeft}>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  title="First Page"
                  aria-label="First Page"
                >
                  <ChevronsLeft size={16} />
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous Page"
                  aria-label="Previous Page"
                >
                  <ChevronLeft size={16} />
                </button>
              </div>

              <div className={styles.paginationCenter}>
                <span className={styles.paginationText}>
                  Page{' '}
                  <strong>{currentPage}</strong>
                  {' '}of{' '}
                  <strong>{totalPages}</strong>
                </span>
                <form onSubmit={handleGoToPage} className={styles.goToPageForm}>
                  <span className={styles.paginationText}>Go to page:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    defaultValue={currentPage}
                    className={styles.pageInput}
                    aria-label="Page number"
                  />
                  <button type="submit" className={styles.goToPageButton}>
                    Go
                  </button>
                </form>
                <div className={styles.pageSizeSelector}>
                  <label htmlFor="pageSize" className={styles.paginationText}>Show:</label>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className={styles.pageSizeSelect}
                  >
                    {[10, 20, 30, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.paginationRight}>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next Page"
                  aria-label="Next Page"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last Page"
                  aria-label="Last Page"
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ActivityLog;
